import React, { useEffect, useRef } from 'react';
import { FiArrowUpRight } from 'react-icons/fi';
import { RxChevronDown, RxChevronRight } from 'react-icons/rx';

import { useRecoilValue } from 'recoil';
import { timestampsState } from 'recoils/common/menuState';
import styled from 'styled-components';

import Tag from 'components/common/Tag';

import useLeftSideBar from 'hooks/menu/useLeftSideBar';

import { findParentPathFromLeftSideMenu } from 'utils/menu/lnbUtil';
import { removeSlashPath } from 'utils/menu/pathUtil';

import { scrollBar } from 'styles/commons/scrollBar';
import { text } from 'styles/commons/text';
import theme from 'styles/token/color';
import { typography } from 'styles/token/typography';

/**
 * 내/외부 링크를 판단하여 적절한 컴포넌트(text.HoverLink 또는 div)를 리턴하는 헬퍼 컴포넌트
 */
const LeftSideBarLink = React.forwardRef(
  ({ isNewTab, path, children, ...props }, ref) => {
    if (!isNewTab) {
      return (
        <div ref={ref} {...props}>
          {children}
        </div>
      );
    }

    const isInternal = path?.startsWith('/');
    if (isInternal) {
      return (
        <text.HoverLink
          ref={ref}
          to={path}
          target="_blank"
          rel="noopener noreferrer"
          {...props}
        >
          {children}
        </text.HoverLink>
      );
    }

    return (
      <text.HoverLink
        ref={ref}
        as="a"
        href={path}
        target="_blank"
        rel="noopener noreferrer"
        {...props}
      >
        {children}
      </text.HoverLink>
    );
  }
);

/**
 * @desc 사이드바 컴포넌트입니다. lnbState를 기반으로 메뉴를 렌더링합니다.
 */
const LeftSideBar = () => {
  const timestamps = useRecoilValue(timestampsState);
  const sidebarRef = useRef(null);
  const activeItemRef = useRef(null);
  const {
    location,
    lnbState,
    onMenuClick,
    onNewTabClick,
    expandedItems,
    onToggleExpand,
  } = useLeftSideBar();

  const currentPath = removeSlashPath(
    location.pathname.replace(/\/(success|fail)$/, '')
  );
  const activePath = findParentPathFromLeftSideMenu(currentPath) || currentPath;

  // ✅ maxHeight 재계산 (스크롤 + 리사이즈 + 컨텐츠 높이 변화)
  useEffect(() => {
    const updateMaxHeight = () => {
      if (!sidebarRef.current) return;
      const footer = document.getElementById('footer');
      if (footer) {
        const footerTop = footer.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;
        if (footerTop < windowHeight) {
          sidebarRef.current.style.maxHeight = `${Math.max(0, footerTop - 90 - 24)}px`;
        } else {
          sidebarRef.current.style.maxHeight = `calc(100vh - 90px)`;
        }
      } else {
        sidebarRef.current.style.maxHeight = `calc(100vh - 90px)`;
      }
    };

    let rafId = null;
    const scheduleUpdate = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateMaxHeight);
    };

    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate, { passive: true });

    const contentEl = document.querySelector('.inavi_content');
    let resizeObserver = null;
    if (contentEl) {
      resizeObserver = new ResizeObserver(scheduleUpdate);
      resizeObserver.observe(contentEl);
    }

    updateMaxHeight();

    return () => {
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      if (resizeObserver) resizeObserver.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  // ✅ 핵심: 페이지 스크롤 시에도 active 메뉴가 LNB 안에서 항상 보이도록 유지
  useEffect(() => {
    const scrollActiveIntoView = () => {
      if (!activeItemRef.current || !sidebarRef.current) return;

      const sidebar = sidebarRef.current;
      const activeItem = activeItemRef.current;

      const sidebarRect = sidebar.getBoundingClientRect();
      const itemRect = activeItem.getBoundingClientRect();

      const isAbove = itemRect.top < sidebarRect.top;
      const isBelow = itemRect.bottom > sidebarRect.bottom;

      if (isAbove || isBelow) {
        // LNB 내부 스크롤을 active 아이템이 중앙에 오도록 이동
        const scrollTarget =
          activeItem.offsetTop -
          sidebar.clientHeight / 2 +
          activeItem.offsetHeight / 2;

        sidebar.scrollTo({
          top: Math.max(0, scrollTarget),
          behavior: 'smooth',
        });
      }
    };

    // 1) activePath 변경 시 (메뉴 클릭, 페이지 진입): 애니메이션 후 실행
    const timer = setTimeout(scrollActiveIntoView, 250);

    // 2) 페이지 스크롤 시에도 active 메뉴가 TOC처럼 따라오도록 감지
    let rafId = null;
    const onPageScroll = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(scrollActiveIntoView);
    };

    window.addEventListener('scroll', onPageScroll, { passive: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', onPageScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [activePath]);

  if (!lnbState || !lnbState.lnb || lnbState.lnb.length === 0) {
    return null;
  }

  return (
    <SidebarWrap>
      <SidebarWrapper className="sidebar_wrapper" ref={sidebarRef}>
        {lnbState.title && <SidebarTitle>{lnbState.title}</SidebarTitle>}
        <SidebarNav>
          {lnbState.lnb.map((item, index) => {
            // '서비스별 요금'은 LNB에서 경로 이동 및 활성화 처리 대신 펼침/접힘 동작만 하도록 예외 처리
            const isServiceBillingParent = item.title === '서비스별 요금';
            const effectivePath = isServiceBillingParent ? undefined : item.path;

            const isParentActive =
              activePath === effectivePath ||
              (effectivePath &&
                effectivePath !== '/' &&
                activePath.startsWith(`${effectivePath}/`));
            const hasSubContent = item.content && item.content.length > 0;
            const isExpanded = expandedItems[index];

            const isItemNewTab =
              item.isNewTab || item.isAbsolute || item.type === 'link';
            const targetUrl = effectivePath || item.link;

            return (
              <React.Fragment key={index}>
                <MenuItemContainer
                  as={LeftSideBarLink}
                  isNewTab={isItemNewTab}
                  path={targetUrl}
                  ref={isParentActive && !hasSubContent ? activeItemRef : null}
                  className={isParentActive ? 'active' : ''}
                  onClick={(e) => {
                    if (!targetUrl) {
                      return onToggleExpand(e, index);
                    } else if (isItemNewTab) {
                      // isItemNewTab인 경우 LeftSideBarLink(text.HoverLink)의 기본 링크(a 태그 target="_blank")로 동작하므로
                      // onClick에서 window.open을 추가로 호출하지 않습니다.
                      return;
                    } else {
                      return onMenuClick(item);
                    }
                  }}
                  $clickable={!!effectivePath}
                >
                  <SidebarItemContent className="content">
                    <MenuItem>
                      {item.title}
                      {(item.isNewTab || item.isAbsolute) && (
                        <FiArrowUpRight className="arrow-top" />
                      )}
                      <Tag.Menu
                        item={
                          timestamps?.[item.tagType] || {
                            createdAt: item.createdAt,
                            updatedAt: item.updatedAt,
                          }
                        }
                      />
                    </MenuItem>
                  </SidebarItemContent>
                  {hasSubContent && (
                    <span className="arrow-down-right">
                      {isExpanded ? <RxChevronDown /> : <RxChevronRight />}
                    </span>
                  )}
                </MenuItemContainer>
                {hasSubContent && (
                  <SubMenuNav
                    style={{
                      height: isExpanded ? item.content.length * 38 + 4 : 0,
                    }}
                  >
                    {item.content.map((sub, subIdx) => {
                      const isSubActive =
                        activePath === sub.path ||
                        (sub.path &&
                          sub.path !== '/' &&
                          activePath.startsWith(`${sub.path}/`));
                      const isSubNewTab =
                        sub.isNewTab || sub.isAbsolute || sub.type === 'link';

                      return (
                        <SubMenuItem
                          as={LeftSideBarLink}
                          isNewTab={isSubNewTab}
                          path={sub.path}
                          ref={isSubActive ? activeItemRef : null}
                          key={subIdx}
                          className={isSubActive ? 'active' : ''}
                          onClick={() => {
                            if (isSubNewTab) {
                              return;
                            } else {
                              return onMenuClick(sub);
                            }
                          }}
                        >
                          <MenuItem>
                            {sub.title}
                            {isSubNewTab && (
                              <FiArrowUpRight className="arrow-top" />
                            )}
                            <Tag.Menu item={sub} />
                          </MenuItem>
                        </SubMenuItem>
                      );
                    })}
                  </SubMenuNav>
                )}
              </React.Fragment>
            );
          })}
        </SidebarNav>
      </SidebarWrapper>
    </SidebarWrap>
  );
};

export default LeftSideBar;

const SidebarWrap = styled.div`
  align-self: flex-start;
  position: sticky;
  top: 66px;

  flex: 0 0 240px;
  width: 240px;
  min-width: 240px;
  padding: 16px 0 24px 0;

  .arrow-top {
    font-size: 1em;
    margin-left: 0px !important;
  }

  .small {
    margin-left: 4px;
  }
`;

const SidebarWrapper = styled.div`
  max-height: calc(100vh - 90px);
  padding-right: 8px;
  overflow-y: auto;
  ${scrollBar.hover};
`;

const SidebarTitle = styled.div`
  height: 40px;
  display: flex;
  align-items: center;
  ${typography.labelLarge};
  color: ${theme.color.gray900};
  line-height: 1;
  font-size: 15px;
`;

const SidebarNav = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const MenuItemContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 40px;
  cursor: pointer;
  transition: all 0.2s;
  margin: 0 0 0 8px;
  border-radius: 6px;

  .arrow-down-right {
    font-size: 16px;
    display: flex;
    align-items: center;
    cursor: pointer;
    color: ${theme.color.gray600};
  }

  &:hover {
    .content {
      color: ${theme.color.gray900};
    }
  }

  &.active {
    .content {
      font-weight: 400;
      color: ${theme.color.primary};
    }
  }
`;

const SidebarItemContent = styled.div`
  color: ${theme.color.gray700};
  ${typography.bodySmall}
  font-weight: 400;
`;

const SubMenuNav = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  overflow: hidden;
  transition: height 0.2s ease-out;
`;

const SubMenuItem = styled.li`
  padding: 8px 16px;
  color: ${theme.color.gray600};
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 6px;
  ${typography.labelLarge};
  font-weight: 300;

  &:hover {
    background: ${theme.color.gray50};
  }

  &.active {
    color: ${theme.color.primary};
    background: ${theme.color.primaryBg};
    font-weight: 400;
  }
`;

const MenuItem = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;
